/**
 * Level Progress Card
 * Visual level badge with XP progress bar - ported from mobile
 */

import { useMemo } from 'react';
import { Medal, Ribbon, Star, Trophy, Diamond, Gift } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { LevelInfo } from '../types';
import { getLevelTierColors, formatXp, getXpToNextLevel } from '../utils/helpers';
import { LEVELS } from '../utils/constants';

interface LevelProgressCardProps {
  levelInfo: LevelInfo;
}

// Static icon components - defined outside component
const LEVEL_ICONS = {
  medal: Medal,
  ribbon: Ribbon,
  star: Star,
  trophy: Trophy,
  diamond: Diamond,
} as const;

// Get icon key based on level
function getLevelIconKey(level: number): keyof typeof LEVEL_ICONS {
  if (level <= 2) return 'medal';
  if (level <= 4) return 'ribbon';
  if (level <= 6) return 'star';
  if (level <= 8) return 'trophy';
  return 'diamond';
}

export function LevelProgressCard({ levelInfo }: LevelProgressCardProps) {
  const { t } = useLanguage();
  const { level, title, tier, currentXp, progress } = levelInfo;

  const tierColors = getLevelTierColors(tier);
  const iconKey = useMemo(() => getLevelIconKey(level), [level]);
  const LevelIcon = LEVEL_ICONS[iconKey];
  const xpToNext = getXpToNextLevel(currentXp);
  const isMaxLevel = level >= LEVELS.length;
  const nextLevel = !isMaxLevel ? LEVELS[level] : null;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      <div className="p-6">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          {/* Level Badge */}
          <div className="relative">
            {/* Outer glow */}
            <div
              className={`absolute inset-0 rounded-full bg-gradient-to-br ${tierColors.gradient} blur-xl opacity-30`}
            />

            {/* Main badge */}
            <div
              className={`relative w-28 h-28 rounded-full bg-gradient-to-br ${tierColors.gradient} flex items-center justify-center shadow-lg`}
            >
              <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center">
                <LevelIcon className={`w-12 h-12 ${tierColors.text}`} />
              </div>
            </div>

            {/* Level number badge */}
            <div
              className={`absolute -bottom-1 -right-1 w-10 h-10 rounded-full bg-gradient-to-br ${tierColors.gradient} flex items-center justify-center text-white font-bold text-lg shadow-md border-2 border-white`}
            >
              {level}
            </div>
          </div>

          {/* Level Info */}
          <div className="flex-1 text-center sm:text-left">
            <div className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${tierColors.text} bg-opacity-10 mb-2`}
                 style={{ backgroundColor: `${tier === 'gold' ? '#fef3c7' : tier === 'silver' ? '#f3f4f6' : tier === 'bronze' ? '#fef3c7' : tier === 'platinum' ? '#f3e8ff' : '#ecfeff'}` }}>
              {t(`challenges.level.tier.${tier}`, `${tier.charAt(0).toUpperCase() + tier.slice(1)} Tier`)}
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">{title}</h2>
            <p className="text-gray-500 mb-4">
              {formatXp(currentXp)} XP {t('challenges.level.total', 'total')}
            </p>

            {/* Progress Bar */}
            {!isMaxLevel && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    {t('challenges.level.progress', 'Progress to Level')} {level + 1}
                  </span>
                  <span className="font-medium text-gray-900">{progress}%</span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${tierColors.gradient} rounded-full transition-all duration-500`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-500">
                  {formatXp(xpToNext)} XP {t('challenges.level.toNext', 'to next level')}
                </p>
              </div>
            )}

            {/* Max level message */}
            {isMaxLevel && (
              <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg">
                <Diamond className="w-5 h-5 text-cyan-600" />
                <span className="text-sm font-medium text-cyan-700">
                  {t('challenges.level.maxLevel', "You've reached the maximum level!")}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Next Rewards Preview */}
        {nextLevel && nextLevel.rewards.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Gift className="w-4 h-4 text-primary-600" />
              {t('challenges.level.nextRewards', 'Rewards at Level')} {level + 1}
            </h3>
            <div className="flex flex-wrap gap-2">
              {nextLevel.rewards.map((reward, index) => (
                <span
                  key={index}
                  className="px-3 py-1.5 bg-primary-50 text-primary-700 text-sm rounded-full"
                >
                  {reward}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
